/**
 * The hand's tour sheet on the phone (fieldiq-api#1282, WS-2). SNAKE_CASE
 * reads, mapped field by field into the shared editor's shape (tourValues.ts).
 *
 *   /field/ws/jobs/:id/tours/:workDate   no sheet yet that day: open one
 *                                        (PUT …/field/jobs/:id/tours/:workDate,
 *                                        optionally copying a rig crew), then
 *                                        the sheet's own page replaces this.
 *   /field/ws/tours/:id                  the sheet: edit while a draft, then
 *                                        submit; read only once submitted.
 *
 * The PUT is an upsert — the API, not this screen, decides create or update,
 * and the first sheet on a scheduled job moves it to moving in. A future day
 * is refused (future_day) and a hand not on the crew is refused (not_on_crew);
 * both are shown as sentences. Hours are the API's.
 *
 * WS-3: under the sheet, its string events and tallies (WsFieldString) —
 * written while a draft, read only once submitted.
 *
 * fieldiq-api#1324: above the string, the sheet's BOP tests and drills with a
 * Pending / Approved badge; the rig manager or driller on the sheet records
 * them while it is a draft (WsFieldTourBop).
 */
import React, { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { useWsActivityCodes, useWsMeta, useWsRig } from '../../../hooks/useWellServicing';
import { useSaveWsFieldTour, useSubmitWsFieldTour, useWsFieldMyTours, useWsFieldTour, useWsJob } from '../../../hooks/useWsJobs';
import { canWriteWsTours, wsApiError, wsDay, wsJobRefusal, wsTourStatusSkin } from '../../../lib/well-servicing';
import type { WsFieldMeterWarning } from '../../../types/well-servicing';
import {
  PT_FIELD_PRIMARY,
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldPage,
  PtFieldQueryState,
  PtRefusal,
} from '../../pressure-testing/field/PtFieldBits';
import { TourEditor, TourMeterWarnings, TourReadout, TourSummary, tourCatalog, tourDefaults } from '../TourEditor';
import { fieldTourValues, tourCrewOptions, tourUnitOptions } from '../tourValues';
import { WS_INPUT, WsDialog, WsField, WsFormError } from '../WsBits';
import { WsFieldStringSection } from './WsFieldString';
import { WsFieldTourBopSection } from './WsFieldTourBop';

const notOnCrew = (err: unknown) => {
  const e = wsApiError(err);
  return e.status === 403 ? wsJobRefusal(err, 'You cannot open this tour sheet.') : null;
};

/** /field/ws/jobs/:id/tours/:workDate — open the day's sheet. */
export function WsFieldTourDayScreen() {
  const { id, workDate } = useParams<{ id: string; workDate: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const mine = useWsFieldMyTours();
  const job = useWsJob(id);
  const rig = useWsRig(job.data?.data?.rigId ?? undefined);
  const save = useSaveWsFieldTour();
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const existing = (mine.data?.data.tours ?? []).find((t) => t.job_id === id && t.work_date === workDate);
  const crews = rig.data?.data?.crews ?? [];
  const canWrite = canWriteWsTours(user?.role);
  const refused = notOnCrew(job.error);

  if (existing) return <Navigate to={`/field/ws/tours/${existing.id}`} replace />;

  return (
    <PtFieldPage title={`Tour sheet · ${wsDay(workDate)}`} backTo="/field/ws" testId="ws-field-tour-day">
      {refused ? (
        <PtRefusal message={refused} testId="ws-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={job} subject="this job">
          {!job.data?.data ? (
            <PtRefusal message="That job no longer exists. It may have been deleted." />
          ) : (
            <PtFieldCard className="space-y-4 px-4 py-4">
              <p className="text-sm text-fieldiq-text">
                {job.data.data.jobNumber} · {job.data.data.rigNumber || '—'} · {job.data.data.wellName || '—'}
              </p>
              <p className="text-sm text-fieldiq-text">No tour sheet is open for this rig on {wsDay(workDate)}.</p>
              <WsFormError message={error} />
              {canWrite ? (
                <>
                  <WsField label="Copy crew from" hint="The rig's crews; change it on the sheet after">
                    <select className={WS_INPUT} aria-label="Copy crew from" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                      <option value="">{rig.isLoading ? 'Loading…' : crews.length ? 'Do not copy a crew' : 'The rig has no crew yet'}</option>
                      {crews.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} · {c.members.length} {c.members.length === 1 ? 'hand' : 'hands'}
                        </option>
                      ))}
                    </select>
                  </WsField>
                  <button
                    type="button"
                    className={`${PT_FIELD_PRIMARY} w-full`}
                    disabled={save.isPending}
                    onClick={() => {
                      setError(null);
                      if (!id || !workDate) return;
                      save.mutate(
                        { jobId: id, workDate, body: templateId ? { crew_template_id: templateId } : {} },
                        {
                          onSuccess: (res) => {
                            toast.success('Tour sheet opened');
                            if (res.data?.id) navigate(`/field/ws/tours/${res.data.id}`, { replace: true });
                          },
                          onError: (err) => setError(wsJobRefusal(err, 'The tour sheet could not be opened.')),
                        },
                      );
                    }}
                  >
                    {save.isPending ? 'Opening…' : 'Open the sheet'}
                  </button>
                </>
              ) : (
                <PtRefusal message="Your role cannot open a tour sheet." />
              )}
            </PtFieldCard>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}

/** /field/ws/tours/:id — the sheet. */
export default function WsFieldTourScreen() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const query = useWsFieldTour(id);
  const tour = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const job = useWsJob(tour?.job_id);
  const rig = useWsRig(job.data?.data?.rigId ?? undefined);
  const codes = useWsActivityCodes('true');
  const catalog = useWsMeta().data?.data ?? null;
  const save = useSaveWsFieldTour();
  const submit = useSubmitWsFieldTour();
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<WsFieldMeterWarning[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const refused = notOnCrew(query.error);
  const values = tour ? fieldTourValues(tour) : null;
  const units = values ? tourUnitOptions(job.data?.data ?? null, values.workDate, values.equipment) : [];
  const crewOptions = values ? tourCrewOptions(rig.data?.data ?? null, values.crew) : [];
  const editable = !!tour && tour.status === 'draft' && !tour.locked && canWriteWsTours(user?.role);
  const cat = tourCatalog(catalog);
  const defaults = tourDefaults(meta?.unit_system, meta?.units?.depth);
  const unitName = (equipmentId: string | null) => units.find((u) => u.equipmentId === equipmentId)?.name ?? null;

  return (
    <PtFieldPage title={tour ? `Tour sheet · ${wsDay(tour.work_date)}` : 'Tour sheet'} backTo="/field/ws" testId="ws-field-tour">
      {refused ? (
        <PtRefusal message={refused} testId="ws-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={query} subject="this tour sheet">
          {!tour || !values ? (
            <PtRefusal message="That tour sheet no longer exists. It may have been deleted." />
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <PtFieldBadge skin={wsTourStatusSkin(tour.status, tour.status_label)} />
                <span className="text-sm text-fieldiq-text">
                  {tour.job_number || '—'} · {tour.rig_number || '—'} · {tour.well_name || '—'}
                </span>
              </div>
              {tour.status === 'draft' && tour.returned_reason && (
                <p role="status" data-testid="ws-tour-returned" className="rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text">
                  <span className="font-bold">Returned by the office: </span>
                  {tour.returned_reason}
                </p>
              )}
              <TourMeterWarnings
                warnings={warnings.map((w) => ({ equipmentId: w.equipment_id, entered: w.entered, previous: w.previous, previousDate: w.previous_date ? wsDay(w.previous_date) : null }))}
                unitName={unitName}
              />
              <TourSummary values={values} />
              {editable ? (
                codes.isLoading || job.isLoading ? (
                  <p className="text-sm text-fieldiq-muted">Loading the sheet&apos;s options…</p>
                ) : (
                  <>
                    <TourEditor
                      key={`${tour.id}-${query.dataUpdatedAt}`}
                      values={values}
                      codes={codes.data?.data ?? []}
                      crewOptions={crewOptions}
                      unitOptions={units}
                      catalog={cat}
                      defaults={defaults}
                      pending={save.isPending}
                      error={error}
                      submitLabel="Save sheet"
                      onSubmit={(body) => {
                        setError(null);
                        save.mutate(
                          { jobId: tour.job_id, workDate: tour.work_date, body },
                          {
                            onSuccess: (res) => {
                              toast.success('Tour sheet saved');
                              setWarnings(res.warnings);
                            },
                            onError: (err) => setError(wsJobRefusal(err, 'The tour sheet could not be saved.')),
                          },
                        );
                      }}
                    />
                    <button type="button" className={`${PT_FIELD_SECONDARY} w-full`} onClick={() => { setSubmitError(null); setSubmitting(true); }}>
                      Submit to the office
                    </button>
                  </>
                )
              ) : (
                <TourReadout values={values} catalog={cat} unitOptions={units} />
              )}
              <WsFieldTourBopSection tour={tour} />
              <WsFieldStringSection
                tourId={tour.id}
                tourStatus={tour.status}
                canWrite={!tour.locked && canWriteWsTours(user?.role)}
                catalog={catalog}
                depthUnit={meta?.units?.depth ?? 'm'}
              />
            </div>
          )}
        </PtFieldQueryState>
      )}
      {tour && (
        <WsDialog
          open={submitting}
          title="Submit tour sheet"
          onClose={() => setSubmitting(false)}
          footer={
            <>
              <button type="button" className={PT_FIELD_SECONDARY} onClick={() => setSubmitting(false)} disabled={submit.isPending}>
                Not now
              </button>
              <button
                type="button"
                className={PT_FIELD_PRIMARY}
                disabled={submit.isPending}
                onClick={() =>
                  submit.mutate(tour.id, {
                    onSuccess: () => {
                      toast.success('Tour sheet submitted');
                      setSubmitting(false);
                    },
                    onError: (err) => setSubmitError(wsJobRefusal(err, 'The sheet could not be submitted.')),
                  })
                }
              >
                {submit.isPending ? 'Submitting…' : 'Submit'}
              </button>
            </>
          }
        >
          <WsFormError message={submitError} />
          <p className="text-sm text-fieldiq-text">Submits the saved sheet. Save any changes first. The activities must cover the sheet end to end, and it needs crew.</p>
        </WsDialog>
      )}
    </PtFieldPage>
  );
}
